import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="header-content">
          <div className="logo">
            <span className="logo-icon">🌍</span>
            <h1>Teacha</h1>
          </div>
          <nav className="nav-buttons">
            <button className="btn-login" onClick={() => navigate('/home')}>
              Log In
            </button>
            <button className="btn-signup" onClick={() => navigate('/home')}>
              Sign Up Free
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Learn Languages with
            <span className="gradient-text"> Native Speakers</span>
          </h1>
          <p className="hero-subtitle">
            Practice 1:1 conversations, get AI-powered flashcards, and track your progress—all in one place.
          </p>
          <div className="hero-cta">
            <div className="cta-buttons">
              <div className="cta-button-group">
                <button className="btn-primary-large btn-student" onClick={() => navigate('/home')}>
                  Start Free Trial
                  <span className="btn-subtitle">For Students</span>
                </button>
                <p className="cta-note">7 days free, then $2.99/month</p>
              </div>
              <div className="cta-divider">or</div>
              <div className="cta-button-group">
                <button className="btn-primary-large btn-teacher" onClick={() => navigate('/home')}>
                  Join as Teacher
                  <span className="btn-subtitle">Always Free</span>
                </button>
                <p className="cta-note">Help students practice conversation</p>
              </div>
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">10+</span>
              <span className="stat-label">Languages</span>
            </div>
            <div className="stat">
              <span className="stat-number">1:1</span>
              <span className="stat-label">Video Sessions</span>
            </div>
            <div className="stat">
              <span className="stat-number">AI</span>
              <span className="stat-label">Flashcards</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-icon">🎥</div>
            <h3>1. Schedule Sessions</h3>
            <p>
              Join video meetings with native speakers. Share your personal meeting link
              with instructors to practice real conversations.
            </p>
          </div>
          <div className="step-card">
            <div className="step-icon">🤖</div>
            <h3>2. Auto-Generated Flashcards</h3>
            <p>
              Every conversation is recorded, transcribed, and automatically converted
              into flashcards with key vocabulary and phrases.
            </p>
          </div>
          <div className="step-card">
            <div className="step-icon">📊</div>
            <h3>3. Track Progress</h3>
            <p>
              Review flashcards with spaced repetition, monitor conversation stats,
              and watch your proficiency improve over time.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2 className="section-title">Everything You Need to Master a Language</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">💬</div>
            <h3>Real Conversations</h3>
            <p>Practice with native speakers in 1:1 video sessions</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🗂️</div>
            <h3>Smart Flashcards</h3>
            <p>AI extracts key vocabulary from your conversations</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>Spaced Repetition</h3>
            <p>Review at optimal intervals for maximum retention</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📈</div>
            <h3>Progress Tracking</h3>
            <p>Monitor your improvement with detailed analytics</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔗</div>
            <h3>Shareable Links</h3>
            <p>Easy-to-share meeting links for your instructors</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🌍</div>
            <h3>10+ Languages</h3>
            <p>Spanish, French, German, Italian, Portuguese, and more</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        <div className="pricing-cards-container">
          <div className="pricing-card">
            <div className="pricing-badge">For Students</div>
            <h3>Student Plan</h3>
            <div className="price">
              <span className="price-amount">$2.99</span>
              <span className="price-period">/month</span>
            </div>
            <ul className="pricing-features">
              <li>✓ Unlimited 1:1 video sessions</li>
              <li>✓ Automatic flashcard generation</li>
              <li>✓ Free mobile & desktop app</li>
              <li>✓ Configurable flashcard reminders</li>
              <li>✓ Spaced repetition learning</li>
              <li>✓ Progress analytics</li>
              <li>✓ 10+ languages supported</li>
            </ul>
            <button className="btn-primary-large" onClick={() => navigate('/home')}>
              Start 7-Day Free Trial
            </button>
            <p className="pricing-note">7 days free, then $2.99/month. Cancel anytime.</p>
          </div>

          <div className="pricing-card pricing-card-free">
            <div className="pricing-badge pricing-badge-free">For Teachers</div>
            <h3>Teacher Plan</h3>
            <div className="price">
              <span className="price-amount">Free</span>
              <span className="price-period">forever</span>
            </div>
            <ul className="pricing-features">
              <li>✓ Unlimited video conferencing</li>
              <li>✓ Join student video sessions</li>
              <li>✓ Help students practice conversation</li>
              <li>✓ No account setup required</li>
              <li>✓ Simple guest access</li>
              <li>✓ Support language learners</li>
            </ul>
            <button className="btn-primary-large btn-teacher-pricing" onClick={() => navigate('/home')}>
              Join as Teacher
            </button>
            <p className="pricing-note">Always free. No credit card required.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">🌍</span>
            <span>Teacha</span>
          </div>
          <p className="footer-text">
            Learn languages through real conversations with native speakers.
          </p>
          <div className="footer-links">
            <button className="footer-link" onClick={() => navigate('/legal')}>
              Terms of Service
            </button>
            <span className="footer-separator">•</span>
            <button className="footer-link" onClick={() => navigate('/legal')}>
              Privacy Policy
            </button>
            <span className="footer-separator">•</span>
            <button className="footer-link" onClick={() => navigate('/legal')}>
              Legal Information
            </button>
          </div>
          <p className="footer-copyright">
            © 2025 Teacha. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
